import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { mapAPI } from "services/mapSlice";
import { rebaseLayersToTilesHost } from "services/utils";

import {
  TCWindPath,
  TCWindPathItem,
  TCWindFootprint,
  TCWindOptions,
  TCWindFootprintItem,
} from "../ts-types/TCWindTypes";
import {
  HistoricalHurricaneTrackFilters,
  HistoricalHurricaneWindUnit,
} from "../ts-types/HistoricalHurricaneTrackTypes";
import { SPEED_UNITS } from "src/constants/GlobalConstants";
import {
  PingMapsLayerIntegration,
  LAYER_GOOGLE,
  NHCIdentifyResult,
  OverlayLayer,
  OverlayLayerSubItem,
  PingGoogleMapOverlaySelectedRegionType,
  PingH3HexType,
  VexcelAoI,
  VexcelCollection,
  VexcelReadiness,
} from "@repo/ping-react-js";

export type EagleviewLayerSummary = {
  id: string;
  title: string;
  style: string;
  tileMatrixSet: string;
  format: "png" | "jpeg";
  minZoom?: number;
  maxZoom?: number;
  captureDate?: string;
};

export type NHCIdentifyState = {
  isOpen: boolean;
  isLoading: boolean;
  results: NHCIdentifyResult[];
  error: string | null;
};

type OverlaysInitialState = {
  availableLayers: Partial<Record<PingMapsLayerIntegration, string>> | null;
  overlayLoaded: boolean;
  overlayError: boolean;
  enabledLayer: OverlayLayer;
  selectedHex: PingH3HexType | null;
  hasHexes: boolean;
  nearmapsQueryParameters: Record<string, any>;
  mapTypeId: "roadmap" | "satellite" | "hybrid" | "terrain" | "nearmaps";
  showGooglePOI: boolean;
  selectedRegion: PingGoogleMapOverlaySelectedRegionType | null;
  nhcIdentify: NHCIdentifyState;
  tcWindPathList: TCWindPath[] | null;
  tcWindState: TCWindPathItem | null;
  tcWindBoundingPoints: { lat: number; lng: number }[] | null;
  tcWindMode: boolean;
  tcWindOptions: TCWindOptions;
  tcWindFootprints: TCWindFootprint[];
  tcWindFootprintSelector: TCWindFootprintItem[];
  historicalHurricaneTrackFilters: HistoricalHurricaneTrackFilters;
  selectedHurricaneTrackSid: string | null;
  isPickingHurricaneTrackRadiusCenter: boolean;
  historicalHurricaneWindUnit: HistoricalHurricaneWindUnit;
  vexcelDates: VexcelAoI[] | VexcelCollection[];
  isVexcelReady: VexcelReadiness;
  isVexcelDatesReady: VexcelReadiness;
  selectedVexcelDate: VexcelAoI | VexcelCollection | null;
  eagleviewLayers: EagleviewLayerSummary[];
  selectedEagleviewLayer: EagleviewLayerSummary | null;
  isEagleviewReady: VexcelReadiness;
  tileVersions: Record<string, string> | null;
};

export const DEFAULT_HISTORICAL_HURRICANE_TRACK_FILTERS: HistoricalHurricaneTrackFilters = {
  BasinFilter: ["NA"],
  YearStart: "2005",
  YearEnd: "",
  CategoryFilter: [],
  RadiusCenter: null,
  RadiusMiles: 50,
  TopStormsCount: 30,
  NameSearch: "",
};

const initialState: OverlaysInitialState = {
  availableLayers: null,
  overlayLoaded: false,
  overlayError: false,
  enabledLayer: LAYER_GOOGLE,
  selectedHex: null,
  hasHexes: false,
  nearmapsQueryParameters: {},
  mapTypeId: "roadmap",
  showGooglePOI: true,
  selectedRegion: null,
  nhcIdentify: { isOpen: false, isLoading: false, results: [], error: null },
  tcWindPathList: [],
  tcWindState: null,
  tcWindBoundingPoints: null,
  tcWindMode: false,
  tcWindOptions: {
    UnitName: SPEED_UNITS.KPH,
    TrackEnabled: true,
    FootprintEnabled: true,
    BasinFilter: [],
    YearFilter: [],
  },
  tcWindFootprints: [],
  tcWindFootprintSelector: [],
  historicalHurricaneTrackFilters: DEFAULT_HISTORICAL_HURRICANE_TRACK_FILTERS,
  selectedHurricaneTrackSid: null,
  isPickingHurricaneTrackRadiusCenter: false,
  // IBTrACS reports wind in knots.
  historicalHurricaneWindUnit: "kt",
  vexcelDates: [],
  isVexcelReady: "NOT_AVAILABLE",
  isVexcelDatesReady: "NOT_AVAILABLE",
  selectedVexcelDate: null,
  eagleviewLayers: [],
  selectedEagleviewLayer: null,
  isEagleviewReady: "NOT_AVAILABLE",
  tileVersions: null,
};

const overlaysSlice = createSlice({
  name: "overlays",
  initialState,
  reducers: {
    setAvailableLayers(
      state,
      action: PayloadAction<Record<PingMapsLayerIntegration, string> | null>,
    ) {
      if (import.meta.env?.VITE_APP_ENV === "local" && action.payload) {
        const convertedLinks = Object.fromEntries(
          Object.entries(action.payload).map(
            ([key, value]: [string, string]) => [
              key,
              value.replace("https://", "http://"),
            ],
          ),
        );
        state.availableLayers = convertedLinks;
      } else {
        state.availableLayers = action.payload;
      }
    },
    setOverlayLoaded(state, action: PayloadAction<boolean>) {
      state.overlayLoaded = action.payload;
    },
    setOverlayError(state, action: PayloadAction<boolean>) {
      state.overlayError = action.payload;
    },
    setEnabledLayer(
      state,
      action: PayloadAction<OverlayLayer | OverlayLayerSubItem | null>,
    ) {
      state.enabledLayer = action.payload;
    },
    setSelectedHex(state, action: PayloadAction<PingH3HexType | null>) {
      state.selectedHex = action.payload;
    },
    setNearMapsQueryParameters(state, action) {
      state.nearmapsQueryParameters = {
        ...state.nearmapsQueryParameters,
        ...action.payload,
      };
    },
    setMapTypeId(
      state,
      action: PayloadAction<
        "roadmap" | "satellite" | "hybrid" | "terrain" | "nearmaps"
      >,
    ) {
      state.mapTypeId = action.payload;
    },
    toggleGooglePOI(state) {
      state.showGooglePOI = !state.showGooglePOI;
    },
    setGooglePOI(state, action: PayloadAction<boolean>) {
      state.showGooglePOI = action.payload;
    },
    setSelectedRegion(
      state,
      action: PayloadAction<{
        position: { lat: number; lng: number };
        properties: Record<string, any>;
      } | null>,
    ) {
      state.selectedRegion = action.payload;
    },
    openNHCIdentify(state) {
      state.nhcIdentify = {
        isOpen: true,
        isLoading: true,
        results: [],
        error: null,
      };
    },
    setNHCIdentifyResults(state, action: PayloadAction<NHCIdentifyResult[]>) {
      state.nhcIdentify.isLoading = false;
      state.nhcIdentify.results = action.payload;
      state.nhcIdentify.error = null;
    },
    setNHCIdentifyError(state, action: PayloadAction<string>) {
      state.nhcIdentify.isLoading = false;
      state.nhcIdentify.error = action.payload;
    },
    closeNHCIdentify(state) {
      state.nhcIdentify.isOpen = false;
    },
    setTCWindFootprintSelector(
      state,
      action: PayloadAction<TCWindFootprintItem[]>,
    ) {
      state.tcWindFootprintSelector = action.payload;
    },
    setTCWindFootprints(state, action: PayloadAction<TCWindFootprint[]>) {
      state.tcWindFootprints = action.payload;
    },
    setTCWindOptions(state, action: PayloadAction<TCWindOptions>) {
      state.tcWindOptions = action.payload;
    },
    setTCWindBoundingPoints(
      state,
      action: PayloadAction<{ lat: number; lng: number }[]>,
    ) {
      state.tcWindBoundingPoints = action.payload;
    },
    setTCWindMode(state, action: PayloadAction<boolean>) {
      state.tcWindMode = action.payload;
    },
    setTCWindState(state, action: PayloadAction<TCWindPathItem>) {
      state.tcWindState = action.payload;
    },
    setTCWindPathList(state, action: PayloadAction<TCWindPath[]>) {
      state.tcWindPathList = action.payload;
    },
    setHistoricalHurricaneTrackFilters(
      state,
      action: PayloadAction<HistoricalHurricaneTrackFilters>,
    ) {
      state.historicalHurricaneTrackFilters = action.payload;
    },
    setSelectedHurricaneTrackSid(state, action: PayloadAction<string | null>) {
      state.selectedHurricaneTrackSid = action.payload;
    },
    setIsPickingHurricaneTrackRadiusCenter(state, action: PayloadAction<boolean>) {
      state.isPickingHurricaneTrackRadiusCenter = action.payload;
    },
    setHistoricalHurricaneWindUnit(state, action: PayloadAction<HistoricalHurricaneWindUnit>) {
      state.historicalHurricaneWindUnit = action.payload;
    },
    setVexcelDates(
      state,
      action: PayloadAction<VexcelAoI[] | VexcelCollection[]>,
    ) {
      state.vexcelDates = action.payload;
    },
    setSelectedVexcelDate(
      state,
      action: PayloadAction<VexcelAoI | VexcelCollection>,
    ) {
      state.selectedVexcelDate = action.payload;
    },
    setIsVexcelReady(state, action: PayloadAction<VexcelReadiness>) {
      state.isVexcelReady = action.payload;
    },
    setIsVexcelDatesReady(state, action: PayloadAction<VexcelReadiness>) {
      state.isVexcelDatesReady = action.payload;
    },
    setEagleviewLayers(state, action: PayloadAction<EagleviewLayerSummary[]>) {
      state.eagleviewLayers = action.payload;
    },
    setSelectedEagleviewLayer(
      state,
      action: PayloadAction<EagleviewLayerSummary | null>,
    ) {
      state.selectedEagleviewLayer = action.payload;
    },
    setIsEagleviewReady(state, action: PayloadAction<VexcelReadiness>) {
      state.isEagleviewReady = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      mapAPI.endpoints.getPolicy.matchFulfilled,
      (state, action) => {
        state.hasHexes = false;
        if (action.payload?.hexes?.length) {
          state.hasHexes = true;
        }
      },
    );
    builder.addMatcher(
      mapAPI.endpoints.getLayerSettings.matchFulfilled,
      (state, action) => {
        state.availableLayers = rebaseLayersToTilesHost(
          action.payload.pingmaps.layers,
        );
        state.tileVersions = action.payload.pingmaps.tile_versions ?? null;
      },
    );
  },
});

const { actions, reducer } = overlaysSlice;

export const {
  setAvailableLayers,
  setOverlayLoaded,
  setOverlayError,
  setEnabledLayer,
  setSelectedHex,
  setNearMapsQueryParameters,
  setMapTypeId,
  toggleGooglePOI,
  setGooglePOI,
  setSelectedRegion,
  openNHCIdentify,
  setNHCIdentifyResults,
  setNHCIdentifyError,
  closeNHCIdentify,
  setTCWindFootprintSelector,
  setTCWindFootprints,
  setTCWindOptions,
  setTCWindBoundingPoints,
  setTCWindMode,
  setTCWindState,
  setTCWindPathList,
  setHistoricalHurricaneTrackFilters,
  setSelectedHurricaneTrackSid,
  setIsPickingHurricaneTrackRadiusCenter,
  setHistoricalHurricaneWindUnit,
  setVexcelDates,
  setSelectedVexcelDate,
  setIsVexcelReady,
  setIsVexcelDatesReady,
  setEagleviewLayers,
  setSelectedEagleviewLayer,
  setIsEagleviewReady,
} = actions;

export default reducer;
